import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Header from "./components/Header";
import HeroSection from "./components/HeroSection";
import AchievementGallery from "./components/AchievementGallery";
import ExecutiveSummary from "./components/ExecutiveSummary";
import Biography from "./components/Biography";
import CareerTimeline from "./components/CareerTimeline";
import LeadershipImpact from "./components/LeadershipImpact";
import ExpertiseCloud from "./components/ExpertiseCloud";
import Publications from "./components/Publications";
import ProfessionalDocuments from "./components/ProfessionalDocuments";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import { ENRArticle, ASCEArticle, ConstructionDiveArticle, FeaturedRecognitionArticle } from "./pages/Articles";
import CollectiveJourney from "./pages/CollectiveJourney";
import SEOHead, { HomePageSEO, ArticlePageSEO } from "./components/SEOHead";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Scroll to hash on page load
const ScrollToHash = () => {
  const { hash } = useLocation();
  
  useEffect(() => {
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [hash]);
  
  return null;
};

const HomePage = () => {
  useEffect(() => {
    // Smooth scroll for anchor links
    const handleAnchorClick = (e) => {
      const href = e.target.getAttribute('href');
      if (href && href.startsWith('#')) {
        e.preventDefault();
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    };

    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', handleAnchorClick);
    });

    return () => {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.removeEventListener('click', handleAnchorClick);
      });
    };
  }, []);

  return (
    <div className="app-wrapper">
      <HomePageSEO />
      <Header />
      <main>
        <HeroSection />
        <ExecutiveSummary />
        <AchievementGallery />
        <Biography />
        <CareerTimeline />
        <LeadershipImpact />
        <ExpertiseCloud />
        <Publications />
        <ProfessionalDocuments />
        <Contact />
      </main>
      <Footer />
    </div>
  );
};

// Article Page Wrapper with SEO
const ArticlePageWrapper = ({ children, articleKey, path }) => {
  return (
    <div className="app-wrapper">
      <ArticlePageSEO articleKey={articleKey} path={path} />
      <Header />
      <main>
        {children}
      </main>
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <ScrollToHash />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/articles/cross-continental-infrastructure" element={
            <ArticlePageWrapper articleKey="crossContinentalInfrastructure" path="/articles/cross-continental-infrastructure">
              <ENRArticle />
            </ArticlePageWrapper>
          } />
          <Route path="/articles/generational-thinking" element={
            <ArticlePageWrapper articleKey="generationalThinking" path="/articles/generational-thinking">
              <ASCEArticle />
            </ArticlePageWrapper>
          } />
          <Route path="/articles/bridging-cultures" element={
            <ArticlePageWrapper articleKey="bridgingCultures" path="/articles/bridging-cultures">
              <ConstructionDiveArticle />
            </ArticlePageWrapper>
          } />
          <Route path="/articles/featured-recognition" element={
            <ArticlePageWrapper articleKey="featuredRecognition" path="/articles/featured-recognition">
              <FeaturedRecognitionArticle />
            </ArticlePageWrapper>
          } />
          <Route path="/timeline" element={
            <div className="app-wrapper">
              <SEOHead page="timeline" customData={{ path: "/timeline" }} />
              <Header />
              <main>
                <CollectiveJourney />
              </main>
              <Footer />
            </div>
          } />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
